import React from "react";
import { Controller } from "react-hook-form";
import { TextField } from "@mui/material";
import { allowNumericInputOnly, allowNumericPasteInputOnly, formatPhoneNumber, formatSSN } from "../../utils/functions";

/**
 * Reusable controlled TextField using react-hook-form
 *
 * @param {object} props
 * @param {string} props.name - Field name
 * @param {string} props.label - Label text
 * @param {object} props.control - react-hook-form control object
 * @param {object} props.errors - react-hook-form errors object
 * @param {string} [props.type="text"] - Input type
 * @param {boolean} [props.required] - Whether the field is required
 * @param {object} [props.InputLabelProps] - Optional props for the InputLabel
 * @param {boolean} [props.disabled] - Disable the input
 * @param {boolean} [props.isPhoneNumber] - Format input as a phone number
 */
const TextFieldController = ({
  name,
  label,
  control,
  errors,
  trigger,
  type = "text",
  required = false,
  InputLabelProps,
  disabled = false,
  isPhoneNumber = false,
  isSSN = false,
  isNumber = false,
}) => {
  return (
    <Controller
      name={name}
      control={control}
      render={({ field: { value = "", onChange, ...rest } }) => (
        <TextField
          {...rest}
          value={
            isPhoneNumber
              ? formatPhoneNumber(value)
              : isSSN
              ? formatSSN(value)
              : value
          }
          onChange={(e) => {
            const raw = e.target.value;
            const numeric = raw.replace(/\D/g, "");
            const trimmed = isPhoneNumber
              ? numeric.slice(0, 10)
              : isSSN
              ? numeric.slice(0, 9)
              : raw;
            onChange(trimmed);
            if (trigger) trigger(name)
          }}
          fullWidth
          label={label}
          type={type}
          required={required}
          disabled={disabled}
          variant="outlined"
          error={!!errors?.[name]}
          helperText={errors?.[name]?.message}
          InputLabelProps={InputLabelProps}
          onKeyPress={isPhoneNumber || isSSN || isNumber ? allowNumericInputOnly : undefined}
          onPaste={isPhoneNumber || isSSN || isNumber ? allowNumericPasteInputOnly : undefined}
        />
      )}
    />
  );
};

export default TextFieldController;
